// Admin modal: Pending customer approvals + wallet top-up/deduct for existing customers.
import { useEffect, useState, useCallback } from "react";
import api, { extractError } from "@/api/client";
import { X, UserCheck, UserX, Copy, Check, Wallet as WalletIcon, Plus, Minus, Clock, Search, RefreshCw } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("ar", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return ""; }
}

export default function AdminApprovalsModal({ onClose }) {
  const [tab, setTab] = useState("pending"); // pending | wallet
  const [pending, setPending] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approved, setApproved] = useState(null); // {code, user} — shown after approve
  const [walletFor, setWalletFor] = useState(null); // {id, name, balance, transactions}
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, u] = await Promise.all([
        api.get("/admin/pending-customers"),
        api.get("/admin/users?role=customer"),
      ]);
      setPending(p.data.pending || []);
      setCustomers(u.data.users || []);
    } catch (e) { console.warn("approvals load", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (uid) => {
    try {
      const { data } = await api.post(`/admin/customers/${uid}/approve`);
      setApproved(data);
      load();
    } catch (e) { alert(extractError(e)); }
  };
  const reject = async (uid, name) => {
    if (!window.confirm(`رفض طلب ${name}؟ سيُحذف الحساب نهائياً.`)) return;
    try {
      await api.post(`/admin/customers/${uid}/reject`);
      load();
    } catch (e) { alert(extractError(e)); }
  };
  const copyCode = () => {
    if (!approved?.code) return;
    navigator.clipboard?.writeText(approved.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const filteredCustomers = customers.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name || "").toLowerCase().includes(s)
      || (c.phone || "").includes(s)
      || (c.email || "").toLowerCase().includes(s);
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" data-testid="admin-approvals-modal">
      <div className="bg-white w-full max-w-3xl max-h-[92vh] rounded-3xl talabya-shadow-soft overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-primary-brand to-blue-500 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserCheck size={18}/>
            <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>الحسابات والمحافظ</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="approvals-refresh">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""}/>
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="approvals-close">
              <X size={16}/>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[var(--border)]">
          <button onClick={() => setTab("pending")} data-testid="tab-pending"
            className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 ${tab === "pending" ? "border-b-2 border-primary-brand text-primary-brand bg-blue-50" : "text-[var(--text-secondary)]"}`}>
            <Clock size={14}/> طلبات معلّقة {pending.length > 0 && <span className="bg-primary-brand text-white text-[10px] rounded-full px-2 py-0.5">{pending.length}</span>}
          </button>
          <button onClick={() => setTab("wallet")} data-testid="tab-wallet"
            className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 ${tab === "wallet" ? "border-b-2 border-primary-brand text-primary-brand bg-blue-50" : "text-[var(--text-secondary)]"}`}>
            <WalletIcon size={14}/> محافظ الزبائن
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {tab === "pending" && (
            pending.length === 0 ? (
              <div className="text-center text-sm text-[var(--text-secondary)] py-10" data-testid="pending-empty">
                لا توجد طلبات تسجيل معلّقة حالياً.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="pending-list">
                {pending.map((p) => (
                  <li key={p.id} className="p-4 rounded-2xl border border-[var(--border)] bg-[var(--surface-alt)] flex items-center gap-3" data-testid={`pending-${p.id}`}>
                    <div className="w-10 h-10 rounded-xl bg-blue-100 text-primary-brand flex items-center justify-center font-black">
                      {(p.name || "؟").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{p.name}</div>
                      <div className="text-xs text-[var(--text-secondary)]" dir="ltr">{p.phone}</div>
                      {p.email && <div className="text-[11px] text-[var(--text-secondary)] truncate" dir="ltr">{p.email}</div>}
                      <div className="text-[10px] text-[var(--text-secondary)]">تم التسجيل: {fmtDate(p.created_at)}</div>
                    </div>
                    <button onClick={() => approve(p.id)} className="px-3 py-2 rounded-lg bg-primary-brand text-white text-xs font-bold flex items-center gap-1 hover:opacity-90" data-testid={`approve-${p.id}`}>
                      <UserCheck size={12}/> موافقة
                    </button>
                    <button onClick={() => reject(p.id, p.name)} className="px-3 py-2 rounded-lg bg-white border border-red-300 text-red-600 text-xs font-bold flex items-center gap-1 hover:bg-red-50" data-testid={`reject-${p.id}`}>
                      <UserX size={12}/> رفض
                    </button>
                  </li>
                ))}
              </ul>
            )
          )}
          {tab === "wallet" && (
            walletFor ? (
              <WalletPanel {...walletFor} onBack={() => setWalletFor(null)} onChanged={load}/>
            ) : (
              <>
                <div className="relative mb-3">
                  <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"/>
                  <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="ابحث بالاسم أو الهاتف أو البريد..."
                    className="w-full pr-9 pl-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:outline-none focus:border-primary-brand text-sm text-right"
                    data-testid="wallet-search"
                  />
                </div>
                {filteredCustomers.length === 0 ? (
                  <div className="text-center text-sm text-[var(--text-secondary)] py-10" data-testid="wallet-empty">
                    لا يوجد زبائن مطابقون.
                  </div>
                ) : (
                  <ul className="space-y-2" data-testid="wallet-customers-list">
                    {filteredCustomers.slice(0, 100).map((c) => (
                      <li key={c.id} className="p-3 rounded-2xl border border-[var(--border)] bg-[var(--surface-alt)] flex items-center gap-3" data-testid={`cust-row-${c.id}`}>
                        <div className="w-10 h-10 rounded-xl bg-blue-100 text-primary-brand flex items-center justify-center font-black">
                          {(c.name || "؟").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm truncate">{c.name}</div>
                          <div className="text-xs text-[var(--text-secondary)]" dir="ltr">{c.phone}</div>
                        </div>
                        <div className="text-left">
                          <div className="text-lg font-black text-primary-brand">{(c.wallet_balance || 0).toFixed(2)}</div>
                          <div className="text-[10px] text-[var(--text-secondary)]">د.ل</div>
                        </div>
                        <button onClick={() => setWalletFor({ id: c.id, name: c.name, phone: c.phone, email: c.email })}
                          className="px-3 py-2 rounded-lg bg-primary-brand text-white text-xs font-bold flex items-center gap-1 hover:opacity-90"
                          data-testid={`open-wallet-${c.id}`}>
                          <WalletIcon size={12}/> المحفظة
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )
          )}
        </div>
      </div>

      {/* Approved-code overlay */}
      {approved && (
        <div className="absolute inset-0 z-[60] bg-black/60 flex items-center justify-center px-4" onClick={() => setApproved(null)} data-testid="approved-code-modal">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 talabya-shadow-float text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-green-100 text-green-600 flex items-center justify-center mb-3">
              <Check size={32}/>
            </div>
            <h3 className="text-2xl font-black mb-1" style={{fontFamily:"Cairo"}}>تم إصدار كود التفعيل</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              شارك الكود مع <strong>{approved.user?.name}</strong> ({approved.user?.phone}) — يدخله في صفحة التفعيل لتفعيل حسابه.
            </p>
            <div className="bg-blue-50 border-2 border-dashed border-primary-brand rounded-2xl p-6 mb-3">
              <div className="text-xs text-[var(--text-secondary)] mb-1">كود التفعيل</div>
              <div className="text-5xl font-black text-primary-brand tracking-[10px]" style={{fontFamily:"monospace"}} data-testid="approved-code">
                {approved.code}
              </div>
            </div>
            <button onClick={copyCode} className="w-full py-3 rounded-xl bg-primary-brand text-white font-black flex items-center justify-center gap-2 hover:opacity-90 mb-2" data-testid="copy-code">
              {copied ? <><Check size={16}/> تم النسخ</> : <><Copy size={16}/> نسخ الكود</>}
            </button>
            <button onClick={() => setApproved(null)} className="w-full py-2 text-sm text-[var(--text-secondary)] hover:text-primary-brand" data-testid="close-approved">
              إغلاق
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function WalletPanel({ id, name, phone, onBack, onChanged }) {
  const [data, setData] = useState({ balance: 0, transactions: [] });
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/admin/users/${id}/wallet`);
      setData(d);
    } catch (e) { console.warn("wallet-view", e); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const doAction = async (action) => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setErr("أدخل مبلغاً صحيحاً"); return; }
    setBusy(true); setErr(""); setMsg("");
    try {
      const { data: r } = await api.post(`/admin/users/${id}/wallet/${action}`, { amount: amt, note });
      setMsg(action === "topup" ? `تم الشحن بنجاح — الرصيد الجديد ${r.balance.toFixed(2)} د.ل` : `تم الخصم بنجاح — الرصيد الجديد ${r.balance.toFixed(2)} د.ل`);
      setAmount(""); setNote("");
      load();
      onChanged?.();
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-wallet-panel">
      <button onClick={onBack} className="text-xs text-primary-brand font-bold" data-testid="wallet-back">← العودة للقائمة</button>
      <div className="rounded-2xl bg-gradient-to-l from-primary-brand to-blue-500 text-white p-5">
        <div className="text-sm text-white/85">{name}</div>
        <div className="text-xs text-white/70" dir="ltr">{phone}</div>
        <div className="mt-3 flex items-baseline gap-2">
          <div className="text-4xl font-black" data-testid="admin-wallet-balance">{(data.balance || 0).toFixed(2)}</div>
          <div className="text-sm">د.ل</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input type="number" step="0.5" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
          placeholder="المبلغ (د.ل)"
          className="px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
          data-testid="wallet-amount"
        />
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="ملاحظة (اختياري)"
          className="px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
          data-testid="wallet-note"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => doAction("topup")} disabled={busy}
          className="py-3 rounded-xl bg-green-600 text-white font-black flex items-center justify-center gap-1 hover:opacity-90 disabled:opacity-60"
          data-testid="wallet-topup-btn">
          <Plus size={16}/> شحن
        </button>
        <button onClick={() => doAction("deduct")} disabled={busy}
          className="py-3 rounded-xl bg-red-600 text-white font-black flex items-center justify-center gap-1 hover:opacity-90 disabled:opacity-60"
          data-testid="wallet-deduct-btn">
          <Minus size={16}/> خصم
        </button>
      </div>
      {msg && <div className="text-sm bg-green-50 text-green-700 px-3 py-2 rounded-lg" data-testid="wallet-msg">{msg}</div>}
      {err && <div className="text-sm bg-red-50 text-red-600 px-3 py-2 rounded-lg" data-testid="wallet-err">{err}</div>}
      <div>
        <div className="text-sm font-black mb-2">سجل العمليات</div>
        {data.transactions?.length === 0 ? (
          <div className="text-center text-xs text-[var(--text-secondary)] py-4">لا توجد عمليات</div>
        ) : (
          <ul className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="admin-wallet-tx-list">
            {data.transactions.map((tx) => (
              <li key={tx.id} className="flex items-center gap-2 text-xs p-2 rounded-lg bg-[var(--surface-alt)]" data-testid={`admin-tx-${tx.id}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${tx.amount >= 0 ? "bg-green-500" : "bg-red-500"}`}/>
                <span className="flex-1 truncate">{tx.note || tx.type}</span>
                <span className={`font-bold ${tx.amount >= 0 ? "text-green-600" : "text-red-600"}`}>
                  {tx.amount >= 0 ? "+" : ""}{tx.amount.toFixed(2)}
                </span>
                <span className="text-[10px] text-[var(--text-secondary)]">{fmtDate(tx.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
